import { computed, signal } from "@preact/signals";

/** Screens of the menu shell. `game` is the in-match container (see state/game.ts). */
export type Screen =
  | "main"
  | "play"
  | "ranked"
  | "browser"
  | "create"
  | "loadouts"
  | "profile"
  | "settings"
  | "game";

/** Navigation stack, last element is the visible screen. Never empty. */
export const screenStack = signal<Screen[]>(["main"]);

export const screen = computed<Screen>(() => {
  const s = screenStack.value;
  return s[s.length - 1] ?? "main";
});

export const canGoBack = computed(() => screenStack.value.length > 1);

/** Push a screen (no-op when it is already on top). */
export function navigate(to: Screen): void {
  const s = screenStack.value;
  if (s[s.length - 1] === to) return;
  screenStack.value = [...s, to];
}

/** Replace the top of the stack. */
export function replaceScreen(to: Screen): void {
  const s = screenStack.value;
  screenStack.value = [...s.slice(0, -1), to];
}

/** Drop the whole history and show `to`. */
export function resetTo(to: Screen): void {
  screenStack.value = [to];
}

/** Pop one screen. Returns false at the root. */
export function goBack(): boolean {
  const s = screenStack.value;
  if (s.length <= 1) return false;
  screenStack.value = s.slice(0, -1);
  return true;
}

// ---- Escape handling ----
//
// Escape is "back". Anything that wants to swallow it first (modal, key capture, unsaved-changes
// guard) pushes a handler; only the newest handler runs. Without handlers Escape pops the screen.

type EscapeHandler = () => void;
const escapeHandlers: EscapeHandler[] = [];

/** Register an Escape handler. Returns the function that removes it. */
export function pushEscapeHandler(handler: EscapeHandler): () => void {
  escapeHandlers.push(handler);
  return () => {
    const i = escapeHandlers.lastIndexOf(handler);
    if (i >= 0) escapeHandlers.splice(i, 1);
  };
}

/** True while a modal, key capture or guard has claimed Escape. */
export function hasEscapeHandler(): boolean {
  return escapeHandlers.length > 0;
}

/** Run by the global keydown listener in App. Returns true when something consumed the key. */
export function handleEscape(): boolean {
  const top = escapeHandlers[escapeHandlers.length - 1];
  if (top) {
    top();
    return true;
  }
  return goBack();
}
